import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { HandWaving, FileArrowUp, CloudArrowUp, CheckCircle, CircleNotch, PenNib } from "@phosphor-icons/react";

const STEPS = ["Consent", "Last year's return", "First upload", "Engagement letter", "Done"];

export function OnboardingWizard({ checklists }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const doneKey = `onboarding_done_${user?.id}`;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [checked, setChecked] = useState(false);
  const [gapsFound, setGapsFound] = useState(null);
  const [letterChecked, setLetterChecked] = useState(false);
  const pyRef = useRef();
  const docRef = useRef();

  const { data: consent } = useQuery({ queryKey: ["consent"], queryFn: () => api.get("/portal/consent").then((r) => r.data) });
  const { data: el } = useQuery({ queryKey: ["engagement-letter"], queryFn: () => api.get("/portal/engagement-letter").then((r) => r.data), enabled: open && step >= 2 });

  useEffect(() => {
    if (consent && !consent.signed && !localStorage.getItem(doneKey)) setOpen(true);
  }, [consent, doneKey]);

  const refreshAll = () => ["consent", "gaps", "portal", "compliance"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const sign = useMutation({
    mutationFn: () => api.post("/portal/consent", { typed_name: name, consent: checked }),
    onSuccess: () => { toast.success("Consent recorded — welcome aboard"); refreshAll(); setStep(1); },
    onError: (e) => toast.error(errText(e)),
  });

  const pyImport = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/portal/py-import", fd); },
    onSuccess: ({ data }) => { setGapsFound(data.gaps_detected); toast.success(`Return read — ${data.gaps_detected} item${data.gaps_detected === 1 ? "" : "s"} to watch this year`); refreshAll(); setStep(2); },
    onError: (e) => toast.error(errText(e)),
  });

  const upload = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("engagement_id", checklists[0].engagement_id);
      return api.post("/documents/upload", fd);
    },
    onSuccess: () => { toast.success("Uploaded — AI is filing it"); refreshAll(); setStep(3); },
    onError: (e) => toast.error(errText(e)),
  });

  const signLetter = useMutation({
    mutationFn: () => api.post(`/portal/sign/${el.envelope.id}`, { typed_name: name, consent: letterChecked }),
    onSuccess: () => { toast.success("Engagement letter signed — you're officially onboard"); qc.invalidateQueries({ queryKey: ["engagement-letter"] }); qc.invalidateQueries({ queryKey: ["portal"] }); setStep(4); },
    onError: (e) => toast.error(errText(e)),
  });

  const finish = () => { localStorage.setItem(doneKey, "1"); setOpen(false); };

  const close = (o) => { if (!o) finish(); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><HandWaving size={20} /> Welcome to your secure portal</DialogTitle>
          <DialogDescription className="sr-only">Guided first-visit setup: consent, prior-year return, first upload</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 mb-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full ${i <= step ? "bg-zinc-900" : "bg-zinc-200"}`} />
              <p className={`text-[9px] mt-1 ${i === step ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>{s}</p>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div data-testid="wizard-step-consent">
            <p className="text-xs text-zinc-600 mb-3">Three quick steps and your CPA team has everything to start. First, federal law (IRC § 7216) requires your consent before our secure AI may analyze your tax information — used only to prepare your returns, never to train public models.</p>
            <Input data-testid="wizard-consent-name" placeholder="Type your full legal name" value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm mb-2" />
            <label className="flex items-start gap-2 text-[11px] text-zinc-600 cursor-pointer mb-3">
              <input data-testid="wizard-consent-checkbox" type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
              <span>I consent under IRC § 7216 to AI-assisted analysis and preparation of my tax information by my firm. This typed name is my electronic signature.</span>
            </label>
            <Button data-testid="wizard-consent-btn" className="w-full" disabled={!name.trim() || !checked || sign.isPending} onClick={() => sign.mutate()}>
              {sign.isPending ? <CircleNotch size={14} className="animate-spin mr-1" /> : null} Sign & continue
            </Button>
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-py">
            <p className="text-xs text-zinc-600 mb-3">Upload last year's filed return (PDF). We'll read it automatically and build your personalized checklist — flagging anything from last year that's missing this year.</p>
            <button data-testid="wizard-py-upload-btn" disabled={pyImport.isPending} onClick={() => pyRef.current?.click()}
              className="w-full rounded-md border border-dashed border-zinc-300 py-6 flex flex-col items-center gap-1.5 text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 transition-colors duration-150">
              {pyImport.isPending ? <CircleNotch size={22} className="animate-spin" /> : <FileArrowUp size={22} />}
              <span className="text-xs">{pyImport.isPending ? "Reading your return…" : "Upload last year's return (PDF)"}</span>
            </button>
            <input ref={pyRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="wizard-py-input"
              onChange={(e) => { if (e.target.files[0]) pyImport.mutate(e.target.files[0]); e.target.value = ""; }} />
            <button data-testid="wizard-py-skip" onClick={() => setStep(2)} className="mt-3 w-full text-[11px] text-zinc-400 underline hover:no-underline">I don't have it handy — skip for now</button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-upload">
            {gapsFound != null && <p className="text-[11px] text-emerald-700 mb-2 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Prior-year profile built — {gapsFound} watch item{gapsFound === 1 ? "" : "s"} added to your gap check.</p>}
            <p className="text-xs text-zinc-600 mb-3">Last step: upload your first document — a W-2, 1099, or anything from your checklist. Our AI files it for you instantly.</p>
            {checklists?.length ? (
              <>
                <button data-testid="wizard-doc-upload-btn" disabled={upload.isPending} onClick={() => docRef.current?.click()}
                  className="w-full rounded-md border border-dashed border-zinc-300 py-6 flex flex-col items-center gap-1.5 text-zinc-600 hover:border-zinc-500 hover:text-zinc-900 transition-colors duration-150">
                  {upload.isPending ? <CircleNotch size={22} className="animate-spin" /> : <CloudArrowUp size={22} />}
                  <span className="text-xs">{upload.isPending ? "Uploading…" : "Upload your first document"}</span>
                </button>
                <input ref={docRef} type="file" className="hidden" data-testid="wizard-doc-input"
                  onChange={(e) => { if (e.target.files[0]) upload.mutate(e.target.files[0]); e.target.value = ""; }} />
              </>
            ) : <p className="text-xs text-zinc-400 italic">Your firm hasn't opened an engagement yet — you can upload as soon as they do.</p>}
            <button data-testid="wizard-doc-skip" onClick={() => setStep(3)} className="mt-3 w-full text-[11px] text-zinc-400 underline hover:no-underline">Skip — I'll upload later</button>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-letter">
            {el?.letter ? (
              <>
                <p className="text-xs text-zinc-600 mb-2">One signature and you're fully onboard: your engagement letter for <span className="font-medium">{el.letter.title.replace("Engagement Letter — ", "")}</span>.</p>
                {el.envelope?.status === "signed" ? (
                  <p className="text-xs text-emerald-700 flex items-center gap-1.5 my-3" data-testid="wizard-letter-signed"><CheckCircle size={14} weight="fill" /> Already signed — you're set.</p>
                ) : (
                  <>
                    <div data-testid="wizard-letter-body" className="rounded-md border border-zinc-200 bg-zinc-50 p-3 max-h-44 overflow-y-auto space-y-2 mb-3">
                      {el.letter.paragraphs.map((p, i) => <p key={i} className="text-[10.5px] leading-relaxed text-zinc-600">{p}</p>)}
                    </div>
                    <Input data-testid="wizard-letter-name" placeholder="Type your full legal name to sign" value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm mb-2" />
                    <label className="flex items-start gap-2 text-[11px] text-zinc-600 cursor-pointer mb-3">
                      <input data-testid="wizard-letter-checkbox" type="checkbox" className="mt-0.5" checked={letterChecked} onChange={(e) => setLetterChecked(e.target.checked)} />
                      <span>I have read the engagement letter and agree to its terms. This typed name is my binding electronic signature.</span>
                    </label>
                  </>
                )}
                {el.envelope?.status === "signed" ? (
                  <Button data-testid="wizard-letter-continue" className="w-full" onClick={() => setStep(4)}>Continue</Button>
                ) : (
                  <Button data-testid="wizard-letter-sign-btn" className="w-full" disabled={!name.trim() || !letterChecked || signLetter.isPending} onClick={() => signLetter.mutate()}>
                    {signLetter.isPending ? <CircleNotch size={14} className="animate-spin mr-1" /> : <PenNib size={14} className="mr-1" />} Sign engagement letter
                  </Button>
                )}
              </>
            ) : (
              <p className="text-xs text-zinc-400 italic my-3">No engagement is open yet — your firm will send the letter when one starts.</p>
            )}
            {el?.envelope?.status !== "signed" && (
              <button data-testid="wizard-letter-skip" onClick={() => setStep(4)} className="mt-3 w-full text-[11px] text-zinc-400 underline hover:no-underline">Review later — it stays on your e-signature board</button>
            )}
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-done" className="text-center py-4">
            <CheckCircle size={36} weight="fill" className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-zinc-900">You're all set</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4">Your checklist, gap check, and compliance questions are ready below. Your CPA team is notified the moment you upload.</p>
            <Button data-testid="wizard-finish-btn" className="w-full" onClick={finish}>Go to my portal</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
